import React, { FC, useCallback, useRef, useState, Fragment } from 'react';
import { AsyncTypeahead, Menu, MenuItem, Highlighter } from 'react-bootstrap-typeahead';
import { createUseStyles } from 'react-jss';
import { FailCodes } from '@packages/models/api/fail-codes';
import { failCodesService } from '@web/services/singletons';
import { groupBy } from 'lodash';
import { Icon, Typography } from '@packages/ui/shared';
import { useAbortController } from '@packages/core/http';
import { getLogger } from '@packages/core/logging';
import { useTranslation } from 'react-i18next';
import colors from '@packages/core/styles/colors';

const logger = getLogger('admin');

interface FailCodeTeamsSearchBarProps {
    isLoading: boolean;
    searchExportFailCode?: FailCodes[];
    selectedOption: any;
    selectedTeamName?: string;
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
    setSearchExportFailCode?: React.Dispatch<React.SetStateAction<any>>;
    setSearchFailCodeFlag?: React.Dispatch<React.SetStateAction<boolean>>;
    setSelectedOption: React.Dispatch<React.SetStateAction<any>>;
}

const useStyles = createUseStyles({
    asyncHeadSearch: {
        maxWidth: '100%',
    },
    clearIcon: {
        position: 'absolute',
        right: 8,
        top: 8,
        zIndex: 2,
        backgroundColor: colors.white,
        color: colors.grayFive,
    },
});

export const FailCodeTeamsSearchBar: FC<FailCodeTeamsSearchBarProps> = ({
    isLoading,
    setIsLoading,
    selectedOption,
    setSelectedOption,
    selectedTeamName,
    setSearchFailCodeFlag,
    searchExportFailCode,
    setSearchExportFailCode,
}) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { abortSignalRef } = useAbortController();
    const searchInputRef = useRef<AsyncTypeahead<any>>(null);
    const [options, setOptions] = useState<Array<any>>([]);

    const queryFailCodes = useCallback(
        (query: string) => {
            setIsLoading(true);

            failCodesService
                .searchFailCode({
                    searchFailCodeParams: {
                        query: query,
                    },
                    signal: abortSignalRef.current,
                    ignoreCache: true,
                })
                .then((response) => {
                    setIsLoading(false);
                    if (!response.success && response.aborted) {
                        return;
                    } else if (!response.success) {
                        throw response.data;
                    }
                    let failCodes = response.data.failCodes;

                    if (selectedTeamName && selectedTeamName !== 'missing Fail Codes') {
                        failCodes = failCodes.filter((failCode) => failCode.grouping === selectedTeamName);
                    } else if (selectedTeamName === 'missing Fail Codes') {
                        failCodes = failCodes.filter((failCode) => failCode.grouping === undefined);
                    }
                    setOptions(failCodes);
                    const el = document.getElementById('fail-code-search');
                    if (!el) {
                        searchInputRef.current?.toggleMenu();
                    }
                })
                .catch((e) => {
                    setIsLoading(false);
                    logger.error('Failed to search fail codes', { error: e, query });
                });
        },
        [abortSignalRef]
    );

    return (
        <AsyncTypeahead
            ref={searchInputRef}
            id="fail-code-search"
            className={classes.asyncHeadSearch}
            onChange={([selected]) => {
                setSelectedOption(selected);
                if (selectedTeamName && setSearchFailCodeFlag) {
                    setSearchFailCodeFlag(true);
                }
                if (searchExportFailCode && setSearchExportFailCode) {
                    setSearchExportFailCode([]);
                }
            }}
            onInputChange={() => {
                searchInputRef.current?.hideMenu();
            }}
            isLoading={isLoading}
            options={options}
            onSearch={queryFailCodes}
            labelKey={(o) => `${o.failCode}`}
            filterBy={() => true}
            useCache={false}
            selected={selectedOption ? [selectedOption] : []}
            renderMenu={(results, menuProps, state) => {
                let index = 0;
                const sections = groupBy(results, (item) => {
                    if (item.failCode) {
                        return t('modals:failCodeSearch.resultSections.failCodes', 'Fail Code');
                    } else {
                        return 'unknown';
                    }
                });

                const items = Object.keys(sections)
                    .filter((n) => n !== 'unknown')
                    .sort()
                    .map((sectionName) => {
                        return (
                            <Fragment key={sectionName}>
                                {index !== 0 && <Menu.Divider />}

                                <Menu.Header>
                                    <Typography variant="label">{sectionName}</Typography>
                                </Menu.Header>

                                {sections[sectionName].map((i) => {
                                    const item = (
                                        <MenuItem key={index} option={i} position={index}>
                                            <Highlighter search={state.text}>
                                                {`${i.failCode} - ${i.description}`}
                                            </Highlighter>
                                        </MenuItem>
                                    );

                                    index += 1;
                                    return item;
                                })}
                            </Fragment>
                        );
                    });

                return <Menu {...menuProps}>{items}</Menu>;
            }}
            placeholder={t('modals:failCodeSearch.searchPlacehodler', 'Search')}
        >
            <div className={classes.clearIcon}>
                <Icon
                    name="magnifying-glass"
                    color="grayFive"
                    onPress={() => (searchInputRef.current as any).inputNode.focus()}
                />
            </div>
        </AsyncTypeahead>
    );
};
